import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { CreateSectionRequest, UpdateSectionRequest } from '@/api/types'
import { menusApi } from '@/api/endpoints/menus'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'

/**
 * Mutation hook to create a new section
 */
export function useCreateSection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      data,
    }: {
      menuId: number
      data: CreateSectionRequest
    }) => menusApi.createSection(menuId, data),
    onSuccess: (_result, variables) => {
      toast.success('Section created successfully')
      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to create section')
      }
    },
  })
}

/**
 * Mutation hook to update a section
 */
export function useUpdateSection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
      data,
    }: {
      menuId: number
      sectionId: number
      data: UpdateSectionRequest
    }) => menusApi.updateSection(menuId, sectionId, data),
    onSuccess: (_result, variables) => {
      toast.success('Section updated successfully')
      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to update section')
      }
    },
  })
}

/**
 * Mutation hook to delete a section
 */
export function useDeleteSection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
    }: {
      menuId: number
      sectionId: number
    }) => menusApi.deleteSection(menuId, sectionId),
    onSuccess: (_result, variables) => {
      toast.success('Section deleted successfully')
      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to delete section')
      }
    },
  })
}
